import { ComputedRef, computed } from "vue";
import { NavigationGuard } from "vue-router";
import { storeToRefs } from "pinia";

import { useRootStore } from "../stores/root";

export interface UsePermissions {
  hasPermission: ComputedRef<boolean>;
  orgHasApp: ComputedRef<boolean>;
}

export type Permissions = string | string[];
export default function usePermissions(
  permissions: Permissions,
  checkEvery: boolean = true
): UsePermissions {
  const store = useRootStore();

  const {
    permissions: userPermissions,
    deniedPermissions,
    isAdmin,
    isOrgAdmin,
    orgApps,
  } = storeToRefs(store);
  let permissionArray: string[] = [];
  if (Array.isArray(permissions)) {
    permissionArray = permissions;
  } else if (permissions) {
    permissionArray.push(permissions);
  }
  // const permissionArray = Array.isArray(permissions) ? permissions : [permissions];

  const hasPermission = computed(() => {
    if (!permissionArray?.length) return true;
    if (!userPermissions.value) return false;
    if (isAdmin.value) return true;
    if (isOrgAdmin.value) return true;

    // if we're not checking every, if they have any, its allowed
    if (!checkEvery) {
      return permissionArray.some((p) =>
        userPermissions.value.includes(p as never)
      );
    }
    // otherwise, if any are denied, its not allowed
    const isDenied = permissionArray.some((p) =>
      deniedPermissions.value.includes(p as never)
    );
    if (isDenied) return false;
    return permissionArray.every((p) =>
      userPermissions.value.includes(p as never)
    );
  });

  const orgHasApp = computed(() => {
    if (!permissionArray?.length) return true;
    if (!orgApps.value?.length) return false;

    if (checkEvery) {
      return permissionArray.every((p) => orgApps.value.includes(p));
    }
    return permissionArray.some((p) => orgApps.value.includes(p));
  });

  return {
    hasPermission,
    orgHasApp,
  };
}

export function addPermissionsToRoute(
  permissions: string | string[],
  checkEvery: boolean = true,
  fallback: string = "/"
): {
  beforeEnter: NavigationGuard;
  permissions: string[];
} {
  return {
    async beforeEnter(_to, _from, next) {
      const store = useRootStore();
      await store.waitForUserToBeLoaded();
      const { hasPermission } = usePermissions(permissions, checkEvery);
      if (hasPermission.value) next();
      else {
        window.location.href = fallback;
      }
    },
    permissions: typeof permissions === "string" ? [permissions] : permissions,
  };
}

export function addAdminOnlyRoute(fallback: string = "/"): {
  beforeEnter: NavigationGuard;
  adminOnly: boolean;
} {
  return {
    async beforeEnter(_to, _from, next) {
      const store = useRootStore();
      await store.waitForUserToBeLoaded();
      if (store.isAdmin || store.isOrgAdmin) next();
      else next(fallback);
    },
    adminOnly: true,
  };
}

export function addTestRoute(fallback: string = "/"): {
  beforeEnter: NavigationGuard;
  test: boolean;
} {
  return {
    async beforeEnter(_to, _from, next) {
      const store = useRootStore();
      await store.waitForUserToBeLoaded();
      if (!store.isAdmin) window.location.href = fallback;
      else next();
    },
    test: true,
  };
}

export function addAppAccessCheck(
  apps: string[],
  fallback: string = "/"
): {
  beforeEnter: NavigationGuard;
} {
  return {
    async beforeEnter(_to, _from, next) {
      const store = useRootStore();
      const { orgApps } = storeToRefs(store);
      const allAppsIncluded = apps.every((app) => orgApps.value.includes(app));
      if (!allAppsIncluded) window.location.href = fallback;
      else next();
    },
  };
}
